import React, { memo, useCallback, useMemo } from 'react';
import type { StepNode, OptionsItem } from '@segmently/schema-models/v2';
import type { OnActionCallback, OnEventCallback } from '../../../types/events';
import { resolveLabel } from '../../../utils/contentResolver';
import type { TemplateVariables } from '../../../utils/contentResolver';
import type { PreviewMode } from '../../../types/previewMode';
import { createListItemSelectEvent } from '../../../utils/eventEmitter';
import { useDeferredAdvance } from '../../../hooks/useDeferredAdvance';
import { mapVerticalAlignment } from '../../../utils/styleMapper';

import { CopyBlock } from '../../shared/CopyBlock';
import { ActionBar } from '../../shared/ActionBar';
import { Background } from '../../shared/Background';
import { HeaderBar } from '../../shared/HeaderBar';
import { OptionsList } from '../../shared/OptionsList';
import { Media } from '../../shared/Media';
import styles from './GridSinglePick.module.css';

export interface GridSinglePickProps {
  step: StepNode;
  locale: string;
  variables?: TemplateVariables;
  resolvedScreenValues?: Record<string, string>;
  selectedIndex?: number | null;
  onSelectionChange?: (index: number | null) => void;
  onAction?: OnActionCallback;
  onEvent?: OnEventCallback;
  editMode?: boolean;
  previewMode?: PreviewMode;
}

export const GridSinglePick = memo(function GridSinglePick({
  step,
  locale,
  variables,
  resolvedScreenValues: _resolvedScreenValues,
  selectedIndex = null,
  onSelectionChange,
  onAction,
  onEvent,
  editMode = false,
  previewMode,
}: GridSinglePickProps): React.ReactElement {
  const { content } = step;

  const selectedIndices = useMemo(
    () => (selectedIndex !== null ? new Set([selectedIndex]) : new Set<number>()),
    [selectedIndex]
  );

  // D-T9b-17: the selection and the tracking event are immediate; only the navigation
  // action waits, so the chosen option is on screen for SELECTED_STATE_ADVANCE_DELAY_MS
  // before the engine replaces the screen.
  const { schedule: scheduleAdvance } = useDeferredAdvance();

  const handleListSelect = useCallback((_item: OptionsItem, index: number) => {
    onSelectionChange?.(index);

    if (onEvent) {
      onEvent(createListItemSelectEvent('list-item-select', step, `list-item-${index}`));
    }

    if (onAction) {
      scheduleAdvance(() => {
        onAction(createListItemSelectEvent('list-item-action', step, `list-item-${index}`));
      });
    }
  }, [onSelectionChange, step, onEvent, onAction, scheduleAdvance]);

  // The option group is a `radiogroup`/`group`; give it the question it is asking so a
  // screen reader announces more than "group".
  const optionsGroupLabel = resolveLabel(content?.copy?.title, locale, variables) ?? undefined;

  const ep = editMode || previewMode === 'live' ? 'content' : undefined;
  const mediaHeightPercentage = content?.featuredMedia?.appearance?.dimensions?.heightPercentage;
  const hasStaticMediaHeight = typeof mediaHeightPercentage === 'number' && mediaHeightPercentage > 0;
  const isMediaTopAligned = content?.featuredMedia?.appearance?.topAlignment === 'top' && !hasStaticMediaHeight;
  const contentStyle: React.CSSProperties = {
    justifyContent: mapVerticalAlignment(content?.options?.appearance?.verticalAlignment),
  };

  return (
    <div
      className={`${styles.container}${previewMode === 'live' ? ` ${styles.liveMode}` : ''}${isMediaTopAligned ? ` ${styles.noPaddingTop}` : ''}${editMode ? ' screen-view--edit-mode' : ''}`}
      data-testid="view-grid-single-pick"
    >

      <Background
        backdrop={content?.canvas?.background}
        locale={locale}
        editPath={ep ? `${ep}.canvas.background` : undefined}
        previewMode={previewMode}
      />

      <HeaderBar
        header={content?.header}
        locale={locale}
        variables={variables}
        step={step}
        onAction={onAction}
        editPath={ep ? `${ep}.header` : undefined}
        absolute={isMediaTopAligned}
      />

      <div className={styles.content} style={contentStyle}>
        {content?.featuredMedia && (
          <Media
            media={content.featuredMedia}
            locale={locale}
            editPath={ep ? `${ep}.featuredMedia` : undefined}
            fillContainer={isMediaTopAligned}
            previewMode={previewMode}
          />
        )}
        <div className={styles.mainContent}>
          <div className={`${styles.mainContentWrapper} ${styles.xPaddings}`}>
            <CopyBlock
              copyBlock={content?.copy}
              locale={locale}
              variables={variables}
              editPath={ep ? `${ep}.copy` : undefined}
            />

            <OptionsList
              options={content?.options}
              locale={locale}
              variables={variables}
              selectedIndices={selectedIndices}
              ariaLabel={optionsGroupLabel}
              onSelect={handleListSelect}
              editPath={ep ? `${ep}.options` : undefined}
              previewMode={previewMode}
              step={step}
            />
          </div>
        </div>
      </div>

      <ActionBar
        actionBar={content?.actionBar}
        locale={locale}
        variables={variables}
        step={step}
        onAction={onAction}
        editPath={ep ? `${ep}.actionBar` : undefined}
      />
    </div>
  );
});

GridSinglePick.displayName = 'GridSinglePick';
